import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdOutlineBookmarkAdd } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../redux/authSlice";
import { IoCall } from "react-icons/io5";

import LoaderSpiner from "../Components/LoaderSpiner";
import styles from "./Styles/ElectionAgentTemplate.module.css";
import ElectionDeleteConfirmationModal from "./ElectionDeleteConfirmationModal";

const API_BASE_URL = "https://prod.toxboxai.com/api/election-agent";

const ElectionAgentTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { token, userId, isAuthenticated } = useSelector((state) => state.auth);

  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [previousLocation, setPreviousLocation] = useState(location.pathname);

  // Authentication check and redirect
  useEffect(() => {
    if (!token || !userId || !isAuthenticated) {
      console.error("ElectionAgentTemplate: Missing or invalid token/userID");
      dispatch(logout());
      navigate("/login");
      return;
    }
  }, [dispatch, navigate, token, userId, isAuthenticated]);

  // Detect navigation back to this page and refresh
  useEffect(() => {
    if (previousLocation !== location.pathname && location.pathname === "/election-agent") {
      setRefreshTrigger(prev => prev + 1);
    }
    setPreviousLocation(location.pathname);
  }, [location.pathname, previousLocation]);

  // Fetch agents with memoization
  const fetchAgents = useCallback(async () => {
    if (!token || !userId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await axios.get(
        `https://prod.toxboxai.com/api/election-agent/by-creator/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            // "x-api-key": "f4dcf8a9-3175-4505-bf97-60bc73200050",
          },
        }
      );

      if (response.status === 401) {
        console.warn("Unauthorized, redirecting to login");
        dispatch(logout());
        navigate("/login");
        return;
      }

      setAgents(response.data.agents || []);
      console.log("Fetched election agents:", response.data.agents);
    } catch (err) {
      console.error("Error fetching agents:", err);
      setError(
        err.response?.data?.error ||
        "Failed to load agents. Please try again."
      );
      if (err.response?.status === 401) {
        dispatch(logout());
        navigate("/login");
      }
      setAgents([]);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate, token, userId]);

  // Initial fetch and refresh on trigger
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents, refreshTrigger]);

  const handleDelete = (agentId) => {
    const agent = agents.find((a) => a._id === agentId);
    if (!agent) return;
    setAgentToDelete(agent);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete || !token || !isAuthenticated) {
      alert("Authentication failed. Please log in again.");
      dispatch(logout());
      navigate("/login");
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/delete/${agentToDelete._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setAgents((prev) => prev.filter((a) => a._id !== agentToDelete._id));
      console.log("Deleted election agent:", agentToDelete._id);
    } catch (err) {
      console.error("Error deleting agent:", err);
      if (err.response?.status === 401) {
        alert("Session expired. Please log in again.");
        dispatch(logout());
        navigate("/login");
      } else {
        alert(
          err.response?.data?.error ||
          "Failed to delete agent. Please try again."
        );
      }
    } finally {
      setAgentToDelete(null);
      setIsDeleteModalOpen(false);
    }
  };

  const handleEditData = useCallback((agent) => {
    if (!agent?._id) {
      console.error("Invalid agent ID for edit");
      return;
    }
    navigate(`/edit-agent/${agent._id}`);
  }, [navigate]);

  const handleCreateAgent = () => {
    navigate("/election/create-agent");
  };

  // Manual refresh function (optional - can be triggered by a refresh button)
  const handleManualRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.textWrapper}>
        <p className={styles.heading1}>Manage your Election Agents</p>
        <p className={styles.description}>Initiate calls to voters efficiently</p>
        {/* Optional refresh button */}
      </div>

      {isLoading ? (
        <LoaderSpiner />
      ) : (
        <div className={styles.grid}>
          <div className={styles.addNew} onClick={handleCreateAgent}>
            <span className={styles.plus}>+</span>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          {Array.isArray(agents) && agents.length > 0 ? (
            agents.map((agent, index) => (
              <div key={agent._id} className={styles.agentCard}>
                <div
                  className={`${styles.topBar} ${index % 2 === 0 ? styles.greenBar : styles.blueBar
                    }`}
                >
                  <div className={styles.topBarCont}>
                    {/* <img
                      src={
                        agent.avatarImage ||
                        "https://imgs.search.brave.com/ePN9f2PGs8dkwTCPQHaIb-c_R4eyUELPwSB6RNLRLfs/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9pbWcu/ZnJlZXBpay5jb20v/ZnJlZS1waG90by91/c2VyLXByb2ZpbGUt/aWNvbi1mcm9udC1z/aWRlXzE4NzI5OS0z/OTU5Ni5qcGc_c2Vt/dD1haXNfaHlicmlk/Jnc9NzQwJnE9ODA"
                      }
                      alt={agent.avatarName || "Unnamed Agent"}
                      className={styles.avatar}
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/150?text=No+Image";
                      }}
                    /> */}
                    <h3 className={styles.agentName}>
                      {agent.avatarName || "Unnamed Agent"}
                    </h3>
                  </div>
                  <div className={styles.icons}>
                    <span
                      className={styles.icon}
                      onClick={() => handleDelete(agent._id)}
                      title="Delete Agent"
                    >
                      <RiDeleteBin6Line />
                    </span>

                  </div>
                </div>

                <p className={styles.agentDescription}>
                  <strong>Phone No.: </strong>
                  {agent.phoneNumber || "No Phone Number"}
                  <br />


                  <strong>Type: </strong>

                  {agent.systemPrompt.promptName || "Unknown"}
                  <br />


                  <strong>Voice: </strong>
                  {typeof agent.voice === "object"
                    ? agent.voice.displayName || agent.voice
                    : agent.voice || "Unknown Voice"}
                  <br />
                </p>
                <button
                  onClick={() => handleEditData(agent)}
                  className={styles.role}
                  disabled={!agent._id}
                >
                  Edit
                </button>

              </div>
            ))
          ) : (
            <p className={styles.subheading}>
              No election agents found. Create a new agent to get started!
            </p>
          )}
        </div>
      )}

      <ElectionDeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAgentToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        agentName={agentToDelete?.avatarName || "Unnamed Agent"}
      />
    </div>
  );
};

export default ElectionAgentTemplate;